import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PreviewAltCard,
  Row,
} from "../../components/Component";
import { useForm } from "react-hook-form";
import { createAdminUserAPI, getAdminLoginUsersAPI, updateAdminLoginUserAPI } from "../../api/user";
import MyInput from "../../MyComponents/Form/MyInput";
import { Badge, Form, FormGroup, Input, Label } from "reactstrap";
import MyButton from "../../MyComponents/Form/MyButton";
import { toast } from "react-toastify";
import ConfirmModal from "../../MyComponents/Modal/ConfirmModal";
import useLogout from "../../Hooks/Logout";
import {
  decrypt,
  formatApiErrorMessage,
  formatApiFailureForToast,
  formatCreateUserDbFailureHint,
  formatDateTimeIST,
} from "../../utils/Utils";
import ApiPaginationDataTable from "../../MyComponents/MyTables/ApiPaginationDataTable";
import MySelect from "../../MyComponents/Form/MySelect";
import MyMobile from "../../MyComponents/Form/MyMobile";
import { adminPermissionOptions, roleList } from "../../assets/constant";
import "./admin-user-form.scss";
import "./admin-user-page.scss";
import { hasAdminPermission } from "../../utils/Utils";

/** Map API user row to selected permission_key strings */
const normalizePermissionKeysFromRow = (row) => {
  if (!row) return [];
  const raw = row.permission_keys ?? row.permissions;
  if (!raw) return [];
  if (Array.isArray(raw) && raw.length) {
    if (raw.includes("*") || raw[0] === "*") return [];
    if (typeof raw[0] === "string") return raw.filter(Boolean);
    return raw.map((p) => p?.permission_key ?? p?.key).filter(Boolean);
  }
  return [];
};

const formatLoginRoleLabel = (row) => {
  const r = String(row?.role || "").toLowerCase();
  if (r === "superadmin" || r === "super_admin") return "Superadmin";
  if (r === "admin") return "Admin";
  return row?.role ? String(row.role) : "—";
};

const loginRoleBadgeClass = (row) => {
  const r = String(row?.role || "").toLowerCase();
  if (r === "superadmin" || r === "super_admin") return "admin-user-role-badge admin-user-role-badge--super";
  if (r === "admin") return "admin-user-role-badge admin-user-role-badge--admin";
  return "admin-user-role-badge admin-user-role-badge--other";
};

/** API expects `admin` | `superadmin` (no underscore). */
const mapRoleValueToApi = (roleValue) => {
  const r = String(roleValue || "").toLowerCase();
  if (r === "super_admin" || r === "superadmin") return "superadmin";
  if (r === "admin") return "admin";
  return r;
};

/** Build body for POST /api/create-user — matches CreateUserSchema (incl. mobile for DB NOT NULL). */
const buildCreateUserPayload = ({ email, password, roleValue, permissionKeys, mobile }) => {
  const username = String(email || "").trim();
  const pwd = String(password || "").trim();
  const role = mapRoleValueToApi(roleValue);
  const perms = (permissionKeys || [])
    .map((p) => String(p).trim().toLowerCase())
    .filter(Boolean);
  const mobileDigits = String(mobile || "")
    .replace(/\D/g, "")
    .slice(0, 15);
  return {
    username,
    password: pwd,
    role,
    status: 1,
    mobile: mobileDigits,
    permissions: role === "superadmin" ? [] : perms,
  };
};

const AdminUser = () => {
  const canView = hasAdminPermission("users.view");
  const canMutate = hasAdminPermission("users.create");
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState("Add");
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [totalRows, setTotalRows] = useState(0);
  // Change Page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const logout = useLogout();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      limit: 25,
      server_search: "",
      email: "",
      permissionKeys: [],
      isLoading: true,
      topProductLoading: true,
      tableLoading: true,
    },
  });
  const limit = watch("limit");
  // pagination
  const handlePerRowsChange = (newPerPage, page) => {
    const rows = isNaN(Number(newPerPage)) ? itemPerPage : newPerPage;
    setItemPerPage(rows);
    setValue("page", page);
  };
  const handlePageChange = (page) => {
    setValue("page", page);
  };
  const serverSearchInput = watch("server_search") ?? "";

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(serverSearchInput.trim());
    }, 450);
    return () => clearTimeout(handler);
  }, [serverSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const selectedRoleValue = watch("role")?.value;
  useEffect(() => {
    if (selectedRoleValue === "super_admin") {
      setValue("permissionKeys", []);
    }
  }, [selectedRoleValue, setValue]);
  const [columns, setColumns] = useState([
    {
      name: "Role",
      selector: (row) => formatLoginRoleLabel(row),
      sortable: true,
      minWidth: "120px",
      cell: (row) => <span className={loginRoleBadgeClass(row)}>{formatLoginRoleLabel(row)}</span>,
    },
    {
      name: "Email",
      selector: (row) => row?.email ?? row?.username ?? "—",
      sortable: true,
      minWidth: "200px",
      cell: (row) => <span className="admin-user-email">{row?.email ?? row?.username ?? "—"}</span>,
    },
    {
      name: "Mobile",
      selector: (row) => row?.mobile || "—",
      sortable: true,
      maxWidth: "140px",
      cell: (row) => <span className="admin-user-mobile">{row?.mobile || "—"}</span>,
    },
    {
      name: "Created At (IST)",
      selector: (row) => formatDateTimeIST(row?.created_at),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => (
        <div style={{ cursor: canMutate ? "pointer" : "default" }} onClick={() => handleToggleStatus(row)}>
          <Badge color={`${row.status ? "success" : "danger"}`}>{row.status ? "Active" : "InActive"}</Badge>
        </div>
      ),
      sortable: true,
      wrap: true,
    },

    {
      name: "Action",
      wrap: true,
      minWidth: "100px",
      cell: (row) => (
        <div className="admin-user-actions">
          {canMutate ? (
            <Button
              color="warning"
              outline
              size="sm"
              className="border-0"
              onClick={() => handleEdit(row)}
              title="Edit account"
            >
              <Icon name="edit" />
            </Button>
          ) : (
            <span className="text-soft small">—</span>
          )}
        </div>
      ),
    },
  ]);
  useEffect(() => {
    const body = {
      search: debouncedSearch,
      page: currentPage,
      limit: itemPerPage,
      ...(statusFilter === "active" ? { status: true } : {}),
      ...(statusFilter === "inactive" ? { status: false } : {}),
    };
    getUser(body);
  }, [itemPerPage, currentPage, debouncedSearch, statusFilter, watch("page")]);

  const getUser = (data) => {
    setIsLoading(true);
    getAdminLoginUsersAPI(data)
      .then((res) => {
        const status = res.data.status;
        if (status === true) {
          const decryptedResponse = res.data.data;
          setValue("limit", decryptedResponse?.pagination?.total);
          setTotalRows(decryptedResponse?.pagination?.total ?? 0);
          setData(decryptedResponse?.items);
          toast.clearWaitingQueue();
          setIsLoading(false);
        } else if (status === false) {
          toast.warning(res.data.message);
          toast.clearWaitingQueue();
          setData([]);
          setTotalRows(0);
          setIsLoading(false);
        } else if (status === "expired") {
          toast.error(res.data.message);
          logout();
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
      });
  };
  const handleToggleStatus = (row) => {
    if (!canMutate) return;
    setValue("selectedRow", row);
    setShowModal(true);
  };
  const handleEdit = (row) => {
    if (!canMutate) return;
    console.log(row, "aaaaaaa");
    setValue("selectedRow", row);
    setValue(
      "role",
      row?.role
        ? roleList.find((item) => {
            const api = String(row.role).toLowerCase();
            if (api === "superadmin" || api === "super_admin") return item.value === "super_admin";
            if (api === "admin") return item.value === "admin";
            return item.value === row.role;
          })
        : null
    );
    setValue("email", row?.email ?? row?.name ?? "");
    setValue("mobile", row?.mobile);
    setValue("password", row?.password || "");
    setValue("permissionKeys", normalizePermissionKeysFromRow(row));
    setIsOpen(true);
    setMode("Edit");
  };
  const onFormSubmit = (data) => {
    const permissionKeys = watch("permissionKeys") || [];
    const emailVal = watch("email")?.trim() || "";
    const mobileVal = String(watch("mobile") || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    const roleOption = watch("role");
    /** Legacy update endpoint (may still expect extra fields). */
    const loginId = watch("selectedRow")?.user_id ?? watch("selectedRow")?.id;
    const updateBody = {
      login_id: loginId,
      user_id: loginId,
      role: roleOption?.value,
      phone: mobileVal,
      mobile: mobileVal,
      email: emailVal,
      username: emailVal,
      password: watch("password"),
      permission_keys: permissionKeys,
      permissions: permissionKeys,
    };
    const bod = {
      search: debouncedSearch,
      page: currentPage,
      limit: itemPerPage,
      ...(statusFilter === "active" ? { status: true } : {}),
      ...(statusFilter === "inactive" ? { status: false } : {}),
    };
    if (mode == "Add") {
      const createPayload = buildCreateUserPayload({
        email: emailVal,
        password: watch("password"),
        roleValue: roleOption?.value,
        permissionKeys,
        mobile: mobileVal,
      });
      console.info("POST /api/create-user payload:", JSON.stringify(createPayload));
      createAdminUserAPI(createPayload)
        .then((res) => {
          const http = res.status;
          const d = res.data;

          if ((http === 201 || http === 200) && d?.status === true) {
            toast.success(d.message || "User created successfully");
            toast.clearWaitingQueue();
            getUser(bod);
            reset();
            setIsOpen(false);
            setValue("isLoading", false);
            return;
          }

          if (d?.status === "expired") {
            setValue("isLoading", false);
            toast.error(d.message);
            toast.clearWaitingQueue();
            logout();
            return;
          }

          setValue("isLoading", false);
          console.warn("create-user failed — HTTP", http, d);
          toast.error(formatCreateUserDbFailureHint(d, http), { autoClose: 14000 });
          toast.clearWaitingQueue();
        })
        .catch((err) => {
          setValue("isLoading", false);
          console.warn("create-user network / no response:", err);
          toast.error(formatApiErrorMessage(err.response?.data) || err.message || "Network error");
        });
    }
    if (mode == "Edit") {
      updateAdminLoginUserAPI(updateBody)
        .then((res) => {
          if (res.data.status === true) {
            toast.success(res.data.message);
            toast.clearWaitingQueue();
            getUser(bod);
            reset();
            setIsOpen(false);
            setValue("isLoading", false);
          } else if (res.data.status === false) {
            setValue("isLoading", false);
            console.warn("update login user failed:", res.data);
            toast.error(formatApiFailureForToast(res.data));
            toast.clearWaitingQueue();
          } else if (res.data.status === "expired") {
            setValue("isLoading", false);
            toast.error(res.data.message);
            toast.clearWaitingQueue();
            logout();
          }
        })
        .catch((err) => {
          setValue("isLoading", false);
          console.warn("update login user request error:", err.response?.data || err);
          toast.error(formatApiErrorMessage(err.response?.data) || err.message || "Network error");
        });
    }
  };
  const confirmStatusChange = (isActivate) => {
    const updatedStatus = isActivate ? true : false;
    const userId = watch("selectedRow").user_id;

    // Optimistically update the status in the data array
    setData((prevData) => {
      return prevData.map((user) => (user.user_id === userId ? { ...user, status: updatedStatus } : user));
    });

    // Prepare the request body for status update
    const formObject = {
      user_id: userId,
      status: updatedStatus,
    };

    updateAdminLoginUserAPI({
      login_id: userId,
      user_id: userId,
      status: updatedStatus,
    })
      .then((res) => {
        const raw = res.data?.status;
        const ok = raw === true || raw === "true";
        const fail = raw === false || raw === "false";
        const legacy = !ok && !fail ? decrypt(raw) : null;

        if (ok || legacy === true) {
          toast.success(res.data.message);
          setShowModal(false);
        } else if (fail || legacy === false) {
          toast.error(res.data.message);
          setShowModal(false);
        } else if (raw === "expired" || legacy === "expired") {
          toast.error(res.data.message);
          logout();
        }
      })
      .catch((err) => {
        console.error("Error updating status:", err);
        toast.error("Something went wrong. Please try again.");
      });
  };
  const clearReportFilters = () => {
    setValue("server_search", "");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const activeFilterSummary =
    statusFilter !== "all" || (serverSearchInput && serverSearchInput.trim().length > 0)
      ? `Filters: ${statusFilter !== "all" ? `status = ${statusFilter}` : "all statuses"}${serverSearchInput?.trim() ? ` · search “${serverSearchInput.trim()}”` : ""}`
      : null;

  return (
    <>
      <Head title={"Admin users"} />
      <Content>
        <div className="admin-user-page">
        <BlockHead size={`sm`}>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page tag="h3">
                Admin accounts
              </BlockTitle>
              <BlockDes className="text-soft">
                Directory of panel logins from the database <code className="small">login</code> table. Search, filter by
                status, export, or add a new admin.
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="">
                {canMutate ? (
                  <Button
                    color="primary"
                    outline
                    className={`  me-n1`}
                    onClick={() => {
                      setIsOpen((pre) => !pre);
                      setMode("Add");
                      reset();
                    }}
                  >
                    <Icon name={isOpen ? "minus" : "plus"} />
                  </Button>
                ) : null}
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <div className="admin-user-page__hero">
          <h4 className="admin-user-page__hero-title text-white">Admin Users</h4>
          <p className="admin-user-page__hero-meta">
            These rows are authentication records for the admin app (not end-users from the <code>users</code> table).
            Registered app users are listed under <strong>Registered User</strong>.
          </p>
          {!isLoading ? (
            <div className="admin-user-page__count" aria-live="polite">
              <Icon name="users-fill" />
              {totalRows === 1 ? "1 account" : `${totalRows} accounts`}
            </div>
          ) : null}
        </div>

        <Block>
          <>
            {!canView ? (
              <PreviewAltCard>
                <div className="text-soft">
                  Permission denied: <code>users.view</code>
                </div>
              </PreviewAltCard>
            ) : null}
            {isOpen && canMutate && (
              <>
                {" "}
                <PreviewAltCard>
                  <Form onSubmit={handleSubmit(onFormSubmit)}>
                    <Row>
                      <Col md="3">
                        <MySelect
                          register={register}
                          watch={watch}
                          setValue={setValue}
                          trigger={trigger}
                          errors={errors}
                          title="Role"
                          name="role"
                          placeholder="Select role"
                          options={roleList}
                        />
                      </Col>
                      <Col md="3" className={``}>
                        <MyInput
                          register={register}
                          watch={watch}
                          setValue={setValue}
                          trigger={trigger}
                          errors={errors}
                          title={"Email"}
                          name={"email"}
                          type="email"
                          placeholder={"Enter email address"}
                          required
                          min={5}
                          max={254}
                          pattern={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
                          patternMsg={"Enter a valid email address"}
                        />
                      </Col>
                      <Col md="3">
                        <MyMobile
                          watch={watch}
                          setValue={setValue}
                          trigger={trigger}
                          errors={errors}
                          register={register}
                          title={"Mobile"}
                          name={"mobile"}
                          required={mode == "Add"}
                        />
                      </Col>
                      <Col md="3" className={``}>
                        <MyInput
                          register={register}
                          watch={watch}
                          setValue={setValue}
                          trigger={trigger}
                          errors={errors}
                          title={"Password"}
                          name={"password"}
                          placeholder={"Enter user password"}
                          required={mode == "Add"}
                          min={2}
                          max={50}
                          // inputPattern={/[^A-Za-z\s]/g}
                          pattern={/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/}
                          patternMsg={
                            "Minimum length of 8 characters, only allowing letters, digits, and special characters"
                          }
                        />
                      </Col>
                    </Row>
                    <Row className="g-gs">
                      <Col xs="12">
                        <div className="admin-user-permissions">
                          <h6 className="title mb-1">Role &amp; permissions</h6>
                          {watch("role")?.value === "super_admin" ? (
                            <p className="text-soft small mb-0">
                              Superadmin accounts get full access. The API does not store rows in{" "}
                              <code className="small">login_permissions</code> for this role.
                            </p>
                          ) : (
                            <>
                          <p className="text-soft small mb-3">
                            Choose which actions this admin can perform. These match{" "}
                            <code className="small">permission_key</code> values in your database.
                          </p>
                          {[...new Set(adminPermissionOptions.map((o) => o.group))].map((group) => (
                            <div key={group} className="mb-3">
                              <div className="admin-user-permissions__group-title">{group}</div>
                              <Row className="g-2">
                                {adminPermissionOptions
                                  .filter((o) => o.group === group)
                                  .map((opt) => {
                                    const selected = (watch("permissionKeys") || []).includes(opt.key);
                                    return (
                                      <Col key={opt.key} xs="12" sm="6" md="4" lg="3">
                                        <FormGroup check className="mb-0">
                                          <Input
                                            type="checkbox"
                                            id={`admin-perm-${opt.key.replace(/\./g, "-")}`}
                                            checked={selected}
                                            onChange={(e) => {
                                              const cur = watch("permissionKeys") || [];
                                              if (e.target.checked) {
                                                setValue("permissionKeys", [...new Set([...cur, opt.key])], {
                                                  shouldDirty: true,
                                                });
                                              } else {
                                                setValue(
                                                  "permissionKeys",
                                                  cur.filter((k) => k !== opt.key),
                                                  { shouldDirty: true }
                                                );
                                              }
                                            }}
                                          />
                                          <Label
                                            check
                                            className="form-check-label"
                                            htmlFor={`admin-perm-${opt.key.replace(/\./g, "-")}`}
                                          >
                                            {opt.label}{" "}
                                            <code className="ms-1 d-block d-md-inline mt-1 mt-md-0">{opt.key}</code>
                                          </Label>
                                        </FormGroup>
                                      </Col>
                                    );
                                  })}
                              </Row>
                            </div>
                          ))}
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs="12">
                        <MyButton isLoading={isLoading} mode={mode} />
                      </Col>
                    </Row>
                  </Form>
                </PreviewAltCard>
              </>
            )}
          </>
          <div className="admin-user-page__table-wrap">
          <ApiPaginationDataTable
            data={data}
            limit={limit}
            totalRows={totalRows}
            setItemPerPage={setItemPerPage}
            itemPerPage={itemPerPage}
            perPage={itemPerPage}
            paginate={paginate}
            currentPage={currentPage}
            columns={columns}
            title={"Admin Users"}
            subtitle="Use search and status filters to narrow the list. Export applies to the current result set."
            isLoading={isLoading}
            register={register}
            trigger={trigger}
            watch={watch}
            setValue={setValue}
            errors={errors}
            serverSearch
            paginationServer
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            expA
            expKeys={["role", "email", "username", "mobile", "created_at", "status", "permission_keys"]}
            fileName={"admin-login-users"}
            searchPlaceholder="Search by email, mobile or role…"
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            showStatusFilter
            onClearFilters={clearReportFilters}
            activeFilterSummary={activeFilterSummary}
          />
          </div>
        </Block>
        </div>
      </Content>
      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showModal}
        toggle={() => setShowModal(!showModal)}
        onConfirm={confirmStatusChange}
        status={watch("selectedRow")?.status}
      />
    </>
  );
};

export default AdminUser;
